// The portal's line to WLX: /api/v1 on the same origin (the gateway passes only /api/v1/partner/), with the
// portal's own cookie (docs/auth.md §3a). Every refusal comes back as an ApiError with WLX's code and,
// where a screen needs it, its params (LOGIN_LOCKED → params.until).

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details: { field: string; code: string; message: string }[] = [],
    public readonly params: Record<string, string> = {},
    /** What to quote to Willems when it goes wrong (H-g05): the id of the request in WLX's log. */
    public readonly correlationId: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** The session is gone: it ran out after 8 hours, or a new password or a reset ended it. */
export function isSessionGone(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401 && err.code === "UNAUTHORIZED";
}

/** The first field problem of a 400, by field name. */
export function fieldProblem(err: unknown, field: string): string | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  return err.details.find((d) => d.field === field)?.code ?? null;
}

// Who hears of every refusal (lib/session.ts: a session that ended shows the modal H-g04), whether the
// call went through TanStack Query or straight from a screen.
const listeners = new Set<(err: ApiError) => void>();

export function onApiError(listener: (err: ApiError) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

interface ErrorBody {
  error?: { code?: string; message?: string; details?: ApiError["details"]; params?: Record<string, string>; correlationId?: string };
}

export async function call<T>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
  extraHeaders: Record<string, string> = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json", ...extraHeaders };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const res = await fetch(`/api/v1${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: "same-origin",
    cache: "no-store",
  });
  return answerOf<T>(res);
}

/** A file as the body itself (a packing slip, §17), with its own Content-Type; the answer as `call` reads it. */
export async function send<T>(method: "POST" | "PUT", path: string, body: Blob, headers: Record<string, string>): Promise<T> {
  const res = await fetch(`/api/v1${path}`, { method, headers: { Accept: "application/json", ...headers }, body, credentials: "same-origin", cache: "no-store" });
  return answerOf<T>(res);
}

async function answerOf<T>(res: Response): Promise<T> {
  const data: unknown = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    const error = (data as ErrorBody | null)?.error;
    const err = new ApiError(
      res.status,
      error?.code ?? "UNKNOWN",
      error?.message ?? res.statusText,
      error?.details ?? [],
      error?.params ?? {},
      error?.correlationId ?? null,
    );
    for (const listener of listeners) listener(err);
    throw err;
  }
  return data as T;
}
